import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import ProjectPageHeader, {
  FolderView,
} from "@pages/Project/components/ProjectPageHeader";
import { ProjectOverview } from "@shared/interfaces/ProjectsFiles";
import ReportsTab from "@pages/Project/components/Tabs/ReportsTab";
import OverviewTab from "@pages/Project/components/Tabs/OverviewTab";
import { FileListPage } from "@pages/Project/components/Tabs/FileUploadTab";
import DeleteProjectModal from "@pages/Project/components/DeleteProjectModal";
import { useSidebar } from "@services/SidebarContext";
import "react-toastify/dist/ReactToastify.css";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { Member } from "@shared/interfaces/User";
import { useForm, FormProvider } from "react-hook-form";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import useFetchCountries from "@pages/Project/utils/useFetchCountries";
import { useProject } from "@utils/useProject";
import { useProjectData } from "@pages/Project/utils/useProjectData";
import { useProjectHeaderActions } from "@pages/Project/utils/useProjectHeaderActions";
import { useProjectDocumentsTab } from "@pages/Project/utils/useProjectDocumentsTab";
import { useProjectReportsTab } from "@pages/Project/utils/useProjectReportsTab";
import ConflictModal from "./components/ConflictModal";
import { toastService } from "@services/ToastService";
import { NotificationsContextType } from "@utils/useNotifications";
import { formatCurrencyValue } from "./utils/utils";
import { hasProjectRole } from "@utils/permissions";
import ProjectPageTabs from "./components/Tabs/components/ProjectPageTabs";

const ProjectPage: React.FC = () => {
  const [isValid, setIsValid] = useState(true);
  const [searchParams] = useSearchParams();
  const { projectId } = useParams<{ projectId: string }>();
  const { sharedToken } = useParams<{ sharedToken: string }>();
  const [refreshFile, setRefreshFile] = useState(0);

  const useNotificationContext = () =>
    useOutletContext<NotificationsContextType>();

  const { notifications } = useNotificationContext();

  const [activeTab, setActiveTab] = useState("Overview");

  const [editedProject, setEditedProject] = useState<ProjectOverview | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);

  const [users, setUsers] = useState<Member[]>([]);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);

  const shareMenuRef = useRef<HTMLDivElement>(null);
  const reducedShareMenuRef = useRef<HTMLDivElement>(null);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const memberButtonRef = useRef<HTMLButtonElement>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const { refreshProjects } = useSidebar();
  const [dropdownFilter, setDropdownFilter] = useState<"Oldest" | "Latest">(
    "Latest"
  );
  const [refreshOpenedFolder, setRefreshOpenedFolder] = useState(0);
  const [refreshGDriveExtFiles, setRefreshGDriveExtFiles] = useState(0);
  const [refreshGdriveFolders, setRefreshGdriveFolders] = useState(0);

  const [refreshOneDriveExtFiles, setRefreshOneDriveExtFiles] = useState(0);
  const [refreshOnedriveFolders, setRefreshOnedriveFolders] = useState(0);

  const [isNewItemMenuOpen, setIsNewItemMenuOpen] = useState(false);
  const [isFolderCreating, setIsFolderCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [folderView, setFolderView] = useState<FolderView | null>(null);

  const [folderLoading, setFolderLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { isCountriesDataLoading, countries } = useFetchCountries();
  const { updateProject, getProjectMembers } = useProject();
  const {
    setSelectedProject,
    selectedProject,
    owner,
    isOwner,
    permissions,
    loading,
    canViewProjectFiles,
    fetchUserAndProject,
    fetchUpdatedProject,
    handleDeleteClick,
  } = useProjectData();

  // Get the value of the URL parameter
  const targetTab = searchParams.get("tab");
  useEffect(() => {
    if (targetTab) {
      setActiveTab(targetTab);
    }
  }, [targetTab]);

  const fetchMembers = async () => {
    if (!projectId) return;

    try {
      const members = await getProjectMembers(projectId);
      setUsers(members);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const {
    handleOutsideClick,
    handleShareButtonClick,
    handleMemberButtonClick,
    handleEditClick,
    toggleDeleteModal,
    handleDiscardChanges,
  } = useProjectHeaderActions({
    isEditing,
    selectedProject,
    setIsEditing,
    setEditedProject,
    fetchUpdatedProject,
    setIsShareMenuOpen,
    setIsMemberModalOpen,
    setIsDeletePopupOpen,
    shareMenuRef,
    reducedShareMenuRef,
    shareButtonRef,
    memberButtonRef,
  });

  useEffect(() => {
    fetchUserAndProject(
      setUsers,
      fetchFiles,
      fetchMembers,
      setFiles,
      activeTab
    );
  }, [projectId, sharedToken]);

  //Share Module
  useEffect(() => {
    setIsShareMenuOpen(false);
    setIsMemberModalOpen(false);
  }, [activeTab]);

  useEffect(() => {
    if (isShareMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else if (isMemberModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    // Cleanup the event listener when unmounting
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isShareMenuOpen, isMemberModalOpen]);

  const handleMemberUpdate = (updatedMembers: Member[]) => {
    setUsers(updatedMembers);
  };
  //

  useEffect(() => {
    setCheckedItems([]);
    setIsEditing(false);
  }, [activeTab, selectedProject]);

  const {
    files,
    setFiles,
    filesLoading,
    checkedItems,
    setCheckedItems,
    isFileSelected,
    uploadingFile,
    fetchFiles,
    handleChooseTemplate,
    handleFileUpload,
    handleDeleteSelectedItems,
    handleDownloadSelectedItems,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    showConflictModal,
    conflictedFiles,
    conflictActions,
    handleConflictAction,
    uploadSingleFile,
    setShowConflictModal,
    clearConflictActions,
    dragging,
    currentPage: currentPage_file,
    setCurrentPage: setCurrentPage_file,
    totalPages: totalPages_file,
    fetchingFolderFiles,
  } = useProjectDocumentsTab(
    activeTab,
    setRefreshOpenedFolder,
    setRefreshGDriveExtFiles,
    setRefreshGdriveFolders,
    folderView?.files || []
  );

  const REPORTS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  const startCreateFolder = () => {
    setIsNewItemMenuOpen(false);
    if (isSaving) return;
    setNewFolderName(`Folder (1)`);
    setIsFolderCreating(true);
  };

  const { reports, loadingReports, totalCount } = useProjectReportsTab(
    activeTab,
    dropdownFilter,
    currentPage,
    REPORTS_PER_PAGE
  );

  const totalPages = Math.ceil(totalCount / REPORTS_PER_PAGE);

  const methods = useForm({
    mode: "all", //  Show errors immediately when the user types
    reValidateMode: "onChange",
    defaultValues: {
      name: selectedProject?.name || "",
      description: selectedProject?.description || "",
      icon: selectedProject?.icon || "",
      countryIds: (selectedProject?.countries ?? []).map((c: any) => c.id),
      startDate: selectedProject?.start_date || "",
      endDate: selectedProject?.end_date || "",
      funders: selectedProject?.funders || "",
      formattedBudget: formatCurrencyValue(selectedProject?.budget || ""),
      geoJson: selectedProject?.geojson_data || null,
      currencyId: selectedProject?.currency_id || undefined,
    },
  });

  // Form Methods
  const {
    reset,
    getValues,
    formState: { isDirty, isValid: isFormValid },
  } = methods;

  useEffect(() => {
    if (selectedProject) {
      reset({
        name: selectedProject?.name || "",
        description: selectedProject?.description || "",
        icon: selectedProject?.icon || "",
        countryIds: (selectedProject?.countries ?? []).map((c: any) => c.id),
        startDate: selectedProject?.start_date || "",
        endDate: selectedProject?.end_date || "",
        funders: selectedProject?.funders || "",
        formattedBudget: formatCurrencyValue(selectedProject?.budget || ""),
        geoJson: selectedProject?.geojson_data || null,
        currencyId: selectedProject?.currency_id || undefined,
      });
    }
  }, [selectedProject, reset]);

  const onSubmit = async (data: any) => {
    const payload = {
      name: data.name,
      description: data.description,
      icon: data.icon,
      country_ids: data.countryIds,
      start_date: data.startDate,
      end_date: data.endDate,
      funders: data.funders,
      budget: `${Number(data.formattedBudget?.replace(/[^\d.]/g, ""))}`, // Convert budget to number
      geojson_data: data.geojson_data,
      currency_id: data.currencyId,
    };
    try {
      if (!projectId) {
        console.error("Project ID is undefined.");
        return;
      }
      const data = await updateProject(projectId, payload);

      setSelectedProject(data); // Update project state
      refreshProjects();
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error saving changes:", error);
      if (error.response) {
        console.error("API Response Data:", error.response.data);
      }
    } finally {
      await fetchUpdatedProject(); // Ensure project data is refreshed
    }
  };

  // Unsaved Changes Modal
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const { handleProceed, handleCancel, isBlocked } = useUnsavedChangesWarning(
    getValues(), // current form values
    methods.control._defaultValues // initial/default values
  );

  useEffect(() => {
    setIsUnsavedChangesModalOpen(isBlocked);
  }, [isBlocked]);

  const changeTabHandler = (tab: string) => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(isDirty);
      setTimeout(() => {
        document
          .getElementById("unsavedModalProceed")
          ?.setAttribute("next-tab", tab);
      }, 500);
      return;
    }
    setActiveTab(tab);
  };

  const handleProceedModal = () => {
    if (isUnsavedChangesModalOpen && !isBlocked) {
      const tab = document
        .getElementById("unsavedModalProceed")
        ?.getAttribute("next-tab");
      setIsUnsavedChangesModalOpen(false);
      if (tab) {
        setActiveTab(tab);
        methods.reset();
      }
    }
    handleProceed();
  };

  const handleCancelModal = () => {
    if (isUnsavedChangesModalOpen && !isBlocked) {
      setIsUnsavedChangesModalOpen(false);
    }
    handleCancel();
  };

  const filesProcessed = () => {
    setShowConflictModal(false);
    if (canViewProjectFiles) {
      toastService.success("Files Uploaded successfully");
    }
  };

  const handleTabClick = (tab: string) => {
    if (isCountriesDataLoading) return;
    changeTabHandler(tab);
  };

  const tabsDisabled = isCountriesDataLoading;

  useEffect(() => {
    if (notifications.length > 0) {
      setRefreshOpenedFolder((prev) => prev + 1);
    }
  }, [notifications]);

  useEffect(() => {
    const cancel = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", cancel);
    window.addEventListener("drop", cancel);
    return () => {
      window.removeEventListener("dragover", cancel);
      window.removeEventListener("drop", cancel);
    };
  }, []);

  const [fileCount, setFileCount] = useState(0);

  useEffect(() => {
    setFileCount(
      (folderView?.files?.length || 0) + (folderView?.folders?.length || 0)
    );
  }, [folderView]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isNewItemMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        setIsNewItemMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isNewItemMenuOpen]);

  return (
    <FormProvider {...methods}>
      <form className="flex-1" onSubmit={methods.handleSubmit(onSubmit)}>
        <UnsavedChangesModal
          onProceed={handleProceedModal}
          onCancel={handleCancelModal}
          isOpen={isUnsavedChangesModalOpen}
        />
        <div
          className="flex w-full h-full relative"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, folderView?.parent_id)}
        >
          <div className="flex-1 px-8 pb-3 flex flex-col">
            <SubTitleHeader
              title={`${
                selectedProject
                  ? selectedProject.icon
                    ? selectedProject.icon
                    : "📊"
                  : "📁"
              } ${loading ? "Loading..." : selectedProject?.name}`}
              notificationStyle="mr-[1rem!important]"
              children={
                <ProjectPageHeader
                  activeTab={activeTab}
                  handleGenerateReport={handleChooseTemplate}
                  checkedItems={checkedItems}
                  handleDeleteSelectedItems={handleDeleteSelectedItems}
                  handleDownloadSelectedItems={handleDownloadSelectedItems}
                  isViewer={
                    !(
                      permissions.includes("CAN_EDIT") ||
                      permissions.includes("CAN_MANAGE_ACCESS") ||
                      isOwner
                    )
                  }
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  toggleDeleteModal={toggleDeleteModal}
                  setDropdownFilter={setDropdownFilter}
                  dropdownFilter={dropdownFilter}
                  canViewProjectFiles={canViewProjectFiles}
                  refreshCloudFiles={() =>
                    setRefreshGDriveExtFiles((prev) => prev + 1)
                  }
                  // NEWLY REQUIRED (already available in parent; just pass through)
                  isOwner={isOwner}
                  permissions={permissions}
                  hasProjectRole={hasProjectRole}
                  handleEditClick={handleEditClick}
                  handleDiscardChanges={handleDiscardChanges}
                  isFormValid={isFormValid}
                  users={users}
                  owner={owner}
                  projectId={projectId}
                  isMemberModalOpen={isMemberModalOpen}
                  isShareMenuOpen={isShareMenuOpen}
                  handleMemberButtonClick={handleMemberButtonClick}
                  handleShareButtonClick={handleShareButtonClick}
                  handleMemberUpdate={handleMemberUpdate}
                  memberButtonRef={memberButtonRef}
                  reducedShareMenuRef={reducedShareMenuRef}
                  shareButtonRef={shareButtonRef}
                  shareMenuRef={shareMenuRef}
                  // For the root "New" block:
                  folderLoading={folderLoading}
                  fileCount={fileCount}
                  folderView={folderView}
                  startCreateFolder={startCreateFolder}
                  // Optional flags you already have:
                  loading={loading}
                  fetchingFolderFiles={fetchingFolderFiles}
                  tabsDisabled={isCountriesDataLoading}
                  refreshGDriveFiles={() => {
                    setRefreshGDriveExtFiles((prev) => prev + 1);
                    setRefreshGdriveFolders((prev) => prev + 1);
                  }}
                  refreshOneDriveFiles={() => {
                    setRefreshOneDriveExtFiles((prev) => prev + 1);
                    setRefreshOnedriveFolders((prev) => prev + 1);
                  }}
                />
              }
              childrenTitle={
                <ProjectPageTabs
                  activeTab={activeTab}
                  handleTabClick={handleTabClick}
                  tabsDisabled={tabsDisabled}
                  loading={loading}
                  canViewProjectFiles={canViewProjectFiles}
                />
              }
            />
            <div className="flex-1 overflow-y-auto pt-8">
              {selectedProject && activeTab === "Overview" && (
                <OverviewTab
                  onSubmit={methods.handleSubmit(onSubmit)}
                  project={isEditing ? editedProject! : selectedProject!}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  descriptionError={descriptionError}
                  setIsValid={setIsValid}
                  methods={methods}
                />
              )}
              {activeTab === "Documents" && canViewProjectFiles && (
                <>
                  <FileListPage
                    folderLoading={folderLoading}
                    setFolderLoading={setFolderLoading}
                    folderView={folderView}
                    setFolderView={setFolderView}
                    checkedItems={checkedItems}
                    setCheckedItems={setCheckedItems}
                    projectId={projectId}
                    filesLoading={filesLoading}
                    currentPage={currentPage_file}
                    setCurrentPage={setCurrentPage_file}
                    totalPages={totalPages_file}
                    refreshFile={refreshFile}
                    handleFileUpload={handleFileUpload}
                    dragging={dragging}
                    handleDeleteSelectedItems={handleDeleteSelectedItems}
                    handleDownloadSelectedItems={handleDownloadSelectedItems}
                    // Local
                    refreshOpenedFolder={refreshOpenedFolder}
                    setRefreshOpenedFolder={setRefreshOpenedFolder}
                    // GDrive
                    refreshGDriveExtFiles={refreshGDriveExtFiles}
                    setRefreshGDriveExtFiles={setRefreshGDriveExtFiles}
                    refreshGdriveFolders={refreshGdriveFolders}
                    setRefreshGdriveFolders={setRefreshGdriveFolders}
                    // OneDrive
                    refreshOneDriveExtFiles={refreshOneDriveExtFiles}
                    setRefreshOneDriveExtFiles={setRefreshOneDriveExtFiles}
                    refreshOnedriveFolders={refreshOnedriveFolders}
                    setRefreshOnedriveFolders={setRefreshOnedriveFolders}
                    //
                    isNewItemMenuOpen={isNewItemMenuOpen}
                    setIsNewItemMenuOpen={setIsNewItemMenuOpen}
                    isFolderCreating={isFolderCreating}
                    setIsFolderCreating={setIsFolderCreating}
                    newFolderName={newFolderName}
                    setNewFolderName={setNewFolderName}
                    // defaultNewFolderName={defaultNewFolderName}
                    isSaving={isSaving}
                    setIsSaving={setIsSaving}
                  />
                </>
              )}
              {activeTab === "Reports" && (
                <>
                  <ReportsTab
                    reports={reports}
                    projectId={projectId || ""}
                    sharedToken={sharedToken}
                    loading={loadingReports}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                  />
                </>
              )}
            </div>

            {uploadingFile && (
              <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 w-2/3 bg-white shadow-lg rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <div>
                    <div className="font-bold">{uploadingFile.name}</div>
                    <div className="text-sm text-gray-400">{`${(
                      uploadingFile.size /
                      1024 /
                      1024
                    ).toFixed(2)} MB`}</div>
                  </div>
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-full h-2 bg-gray-200 rounded relative overflow-hidden">
                    <div className="absolute inset-0 bg-blue-600 rounded animate-indeterminate"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DeleteProjectModal
            isOpen={isDeletePopupOpen}
            selectedProject={selectedProject || undefined}
            deleteInput={deleteInput}
            setDeleteInput={setDeleteInput}
            onClose={() => setIsDeletePopupOpen(false)}
            onConfirm={handleDeleteClick}
          />
          {showConflictModal && conflictedFiles.length > 0 && (
            <ConflictModal
              conflictedFiles={conflictedFiles}
              conflictActions={conflictActions}
              onActionChange={handleConflictAction}
              onSingleFileProcess={uploadSingleFile}
              onComplete={filesProcessed}
              dismissConflictModal={() => {
                setShowConflictModal(false);
              }}
              fetchFiles={fetchFiles}
              clearConflictActions={clearConflictActions}
            />
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default ProjectPage;
